/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Vote
 * File     : Vote.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Vote view and coordinating data fetching.
 *   Exports: Vote
 *   
 *
 * Dependencies:
 *   - ../components/layout/Ads
 *   - ../context/AuthContext
 *   - ../services/api
 *   - ./Home
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *   - socket.io-client
 *
 * Usage:
 *   Use in Router: <Route path="/vote" element={<Vote />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchPoll, castVote, getMovieByImdb, buildApiUrl } from '../services/api';
import { io } from 'socket.io-client';
import Home from './Home';
import { useAuth } from '../context/AuthContext';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, CardMedia, Chip, Alert } from '@mui/material';
import { AdSlot } from '../components/layout/Ads';

const Vote = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pollId = searchParams.get('poll');
  const [poll, setPoll] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [omdb, setOmdb] = useState({});
  const { loading } = useAuth();

  const extractImdbFromAny = (val) => {
    if (!val || typeof val !== 'string') return null;
    const m = val.match(/(tt\d{3,8})/i);
    return m ? m[1] : null;
  };

  const posterUrl = (m) => {
    if (!m?.poster || m.poster === 'N/A') return undefined;
    if (m.poster.startsWith('http')) return m.poster;
    // Assume Plex-relative path
    return buildApiUrl(`/api/plex/image?path=${encodeURIComponent(m.poster)}`);
  };

  useEffect(() => {
    if (!pollId || loading) return;
    fetchPoll(pollId)
      .then(async (p) => {
        setPoll(p);
        const details = {};
        for (const m of p.movies || []) {
          const cleanId = extractImdbFromAny(m.imdbID);
          if (cleanId) {
            try {
              details[cleanId] = await getMovieByImdb(cleanId);
            } catch (_) {}
          }
        }
        setOmdb(details);
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 401) setError('Sign in to view this club poll.');
        else if (status === 403) setError('You are not a member of this club.');
        else setError('Failed to load poll');
      });
  }, [pollId, loading]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!pollId) return;
    // Skip Socket.IO when backend is Worker (no Socket.IO support)
    if ((process.env.REACT_APP_SOCKET_IO_ENABLED || 'true').toLowerCase() === 'false') return;
    const base = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || (window.location.origin.replace(':3000', ':5000'));
    const socket = io(base, {
      path: '/api/socket.io',
      withCredentials: true,
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
    });
    socket.on('connect_error', () => {
      socket.io.opts.reconnection = false;
      socket.disconnect();
    });
    socket.emit('join', { pollId });
    socket.on('vote:cast', (payload) => {
      if (payload?.pollId === pollId) {
        fetchPoll(pollId).then(setPoll).catch(() => {});
      }
    });
    return () => socket.disconnect();
  }, [pollId]);

  const formatRemaining = (endDateStr) => {
    const end = new Date(endDateStr).getTime();
    let diff = Math.max(0, end - now);
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    diff -= days * 24 * 60 * 60 * 1000;
    const hours = Math.floor(diff / (60 * 60 * 1000));
    diff -= hours * 60 * 60 * 1000;
    const minutes = Math.floor(diff / (60 * 1000));
    diff -= minutes * 60 * 1000;
    const seconds = Math.floor(diff / 1000);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const handleVote = async (movieId) => {
    setError('');
    setSubmitting(true);
    try {
      await castVote(pollId, movieId);
      navigate(`/results/${pollId}`);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to vote';
      setError(msg);
      setSubmitting(false);
      // Do not navigate — stay on vote page so the user sees that their vote was not recorded
    }
  };

  if (!pollId) return <Home />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Vote</Typography>
      {error && (
        <Alert severity={error.toLowerCase().includes('already voted') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
          {error.toLowerCase().includes('already voted') && ' Your previous vote still counts.'}
        </Alert>
      )}
      {poll && (
        <>
          <Typography variant="h6" gutterBottom>{poll.title}</Typography>
          {poll.club ? (
            <Typography variant="caption" color="text.secondary" gutterBottom>Club: {poll.club.name}</Typography>
          ) : (poll.clubNameArchived ? (
            <Typography variant="caption" color="text.secondary" gutterBottom>{poll.clubNameArchived}</Typography>
          ) : null)}
          {poll.creator ? (
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Created by {poll.creator.displayName || poll.creator.username}
            </Typography>
          ) : null}
          {poll.endDate && (
            <Box sx={{ mb: 2 }}>
              <Chip color="secondary" label={`Ends in ${formatRemaining(poll.endDate)}`} />
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}><AdSlot slot="0000000004" /></Grid>
            {poll.movies.filter(m => m && m.id).map((m) => (
              <Grid item xs={12} md={6} lg={4} key={m.id}>
                <Card>
                  {posterUrl(m) && (
                    <CardMedia
                      component="img"
                      height="300"
                      image={posterUrl(m)}
                      alt={`${m.title} poster`}
                    />
                  )}
                  <CardContent>
                    <Typography>
                      <a href={`https://www.imdb.com/title/${m.imdbID}/`} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {m.title} ({m.year})
                      </a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(() => {
                        const d = omdb[extractImdbFromAny(m.imdbID)] || {};
                        const duration = d.Runtime && d.Runtime !== 'N/A' ? d.Runtime : (m.runtimeMinutes ? `${m.runtimeMinutes} min` : null);
                        const genres = d.Genre && d.Genre !== 'N/A' ? d.Genre : null;
                        const rating = d.imdbRating && d.imdbRating !== 'N/A' ? d.imdbRating : null;
                        const votes = d.imdbVotes && d.imdbVotes !== 'N/A' ? d.imdbVotes : null;
                        const parts = [];
                        if (duration) parts.push(duration);
                        if (genres) parts.push(genres);
                        if (rating || votes) parts.push(`IMDb ${rating || '-'}${votes ? ` (${votes})` : ''}`);
                        return parts.join(' • ');
                      })()}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button onClick={() => handleVote(m.id)} variant="contained" disabled={new Date(poll.endDate) <= new Date() || submitting}>
                      {submitting ? 'Submitting…' : (new Date(poll.endDate) > new Date() ? 'Vote' : 'Voting Closed')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
            <Grid item xs={12}><AdSlot slot="0000000005" /></Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Vote;


